import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { BankAccountService, CreateBankAccountRequest, PaymentFormAliasDTO } from '../../services/bank-account.service';
import { CurrencyDTO, CurrencyService } from '../../services/currency.service';
import { PaymentFormService } from '../../services/payment-form.service';
import { getPaymentFormLabel, type PaymentFormDTO } from '../../core/models';

@Component({
  selector: 'app-bank-account-form',
  templateUrl: './bank-account-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    FloatLabelModule,
    SelectModule,
    ToggleSwitchModule,
    ButtonModule,
    CardModule,
    TooltipModule
  ]
})
export class BankAccountFormComponent {
  private fb = inject(FormBuilder);
  private service = inject(BankAccountService);
  private currencyService = inject(CurrencyService);
  private paymentFormService = inject(PaymentFormService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);

  loading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingId = signal<number | null>(null);
  currencies = signal<CurrencyDTO[]>([]);
  paymentFormOptions = signal<{ id: number; label: string }[]>([]);

  form = this.fb.group({
    alias: ['', [Validators.required, Validators.maxLength(100)]],
    bankName: ['', [Validators.required, Validators.maxLength(128)]],
    clabe: ['', [Validators.required, Validators.pattern(/^\d{18}$/)]],
    currencyId: [null as number | null, Validators.required],
    settlementAccount: [false],
    dateColumn: ['Fecha'],
    descriptionColumn: ['Descripción'],
    referenceColumn: ['Referencia'],
    debitColumn: ['Cargo'],
    creditColumn: ['Abono'],
    amountColumn: [''],
    signColumn: [''],
    balanceColumn: ['Saldo'],
    headerRow: [1],
    dateFormat: ['dd/MM/yyyy'],
    delimiter: [''],
    counterpartNameColumn: [''],
    counterpartClabeColumn: [''],
    trackCodeColumn: [''],
    orderingBankColumn: [''],
    paymentFormColumn: [''],
    paymentFormAliases: this.fb.array<FormGroup>([])
  });

  get paymentFormAliases() {
    return this.form.controls.paymentFormAliases;
  }

  constructor() {
    this.currencyService.listCurrencies({}, { page: 0, size: 50 }).subscribe({
      next: (res) => this.currencies.set(res.body ?? [])
    });
    this.paymentFormService.listPaymentForms({ state: 'ACTIVE' }, { page: 0, size: 500 }).subscribe({
      next: res => this.paymentFormOptions.set((res.body ?? [])
        .filter((form): form is PaymentFormDTO & { id: number } => form.id != null)
        .map(form => ({ id: form.id, label: getPaymentFormLabel(form) || form.code || String(form.id) })))
    });
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      const id = Number(idParam);
      this.isEditMode.set(true);
      this.editingId.set(id);
      this.load(id);
    }
  }

  load(id: number): void {
    this.loading.set(true);
    this.service.getById(id).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (res) => {
        const body = res.body;
        if (!res.ok || !body) {
          this.error.set(res.message || 'No se encontró la cuenta');
          return;
        }
        const layout = body.statementLayout;
        this.form.patchValue({
          alias: body.alias ?? '',
          bankName: body.bankName ?? '',
          clabe: body.clabe ?? '',
          currencyId: body.currency?.id ?? null,
          settlementAccount: !!body.settlementAccount,
          dateColumn: layout?.dateColumn ?? 'Fecha',
          descriptionColumn: layout?.descriptionColumn ?? 'Descripción',
          referenceColumn: layout?.referenceColumn ?? 'Referencia',
          debitColumn: layout?.debitColumn ?? 'Cargo',
          creditColumn: layout?.creditColumn ?? 'Abono',
          amountColumn: layout?.amountColumn ?? '',
          signColumn: layout?.signColumn ?? '',
          balanceColumn: layout?.balanceColumn ?? 'Saldo',
          headerRow: layout?.headerRow ?? 1,
          dateFormat: layout?.dateFormat ?? 'dd/MM/yyyy',
          delimiter: layout?.delimiter ?? '',
          counterpartNameColumn: layout?.counterpartNameColumn ?? '',
          counterpartClabeColumn: layout?.counterpartClabeColumn ?? '',
          trackCodeColumn: layout?.trackCodeColumn ?? '',
          orderingBankColumn: layout?.orderingBankColumn ?? '',
          paymentFormColumn: layout?.paymentFormColumn ?? ''
        });
        this.paymentFormAliases.clear();
        for (const alias of body.paymentFormAliases ?? []) {
          this.paymentFormAliases.push(this.aliasGroup(alias));
        }
      },
      error: () => this.error.set('Error al cargar la cuenta')
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const request: CreateBankAccountRequest = {
      alias: raw.alias!.trim(),
      bankName: raw.bankName!.trim(),
      clabe: raw.clabe!.trim(),
      currencyId: Number(raw.currencyId),
      settlementAccount: !!raw.settlementAccount,
      statementLayout: {
        headerRow: raw.headerRow ?? 1,
        dateFormat: raw.dateFormat || 'dd/MM/yyyy',
        delimiter: raw.delimiter || undefined,
        dateColumn: raw.dateColumn || undefined,
        descriptionColumn: raw.descriptionColumn || undefined,
        referenceColumn: raw.referenceColumn || undefined,
        debitColumn: raw.debitColumn || undefined,
        creditColumn: raw.creditColumn || undefined,
        amountColumn: raw.amountColumn || undefined,
        signColumn: raw.signColumn || undefined,
        balanceColumn: raw.balanceColumn || undefined,
        counterpartNameColumn: raw.counterpartNameColumn || undefined,
        counterpartClabeColumn: raw.counterpartClabeColumn || undefined,
        trackCodeColumn: raw.trackCodeColumn || undefined,
        orderingBankColumn: raw.orderingBankColumn || undefined,
        paymentFormColumn: raw.paymentFormColumn || undefined
      },
      paymentFormAliases: this.paymentFormAliases.controls.map(group => ({
        id: group.getRawValue().id ?? undefined,
        alias: String(group.getRawValue().alias ?? '').trim(),
        paymentFormId: Number(group.getRawValue().paymentFormId)
      }))
    };
    this.loading.set(true);
    const req$ = this.isEditMode() && this.editingId()
      ? this.service.update(this.editingId()!, request)
      : this.service.create(request);
    req$.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (res) => {
        if (!res.ok) {
          this.toastr.error(res.message || 'No se pudo guardar');
          return;
        }
        this.toastr.success(this.isEditMode() ? 'Cuenta actualizada' : 'Cuenta creada');
        void this.router.navigate(['/bank-accounts']);
      },
      error: (err) => this.toastr.error(err.error?.message || 'Error al guardar')
    });
  }

  cancel(): void {
    void this.router.navigate(['/bank-accounts']);
  }

  addPaymentFormAlias(): void {
    this.paymentFormAliases.push(this.aliasGroup());
  }

  removePaymentFormAlias(index: number): void {
    this.paymentFormAliases.removeAt(index);
  }

  private aliasGroup(value?: PaymentFormAliasDTO): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null as number | null],
      alias: [value?.alias ?? '', [Validators.required, Validators.maxLength(128)]],
      paymentFormId: [value?.paymentFormId ?? null as number | null, Validators.required]
    });
  }
}
